import { MERCADOS_LIMA } from './catalogo';

/**
 * Juntar lo que es lo mismo escrito distinto, **sin fusionar lo que no lo es**.
 *
 * ## El problema, con números
 *
 * La lista cerrada del formulario trae **solo los 10 mercados de Lima**, y la ruta del 11 al
 * 13-set salió a Piura, Chiclayo, Tumbes y Tacna. Las 100 fichas cayeron en «Otro · lo escribo»
 * y siete mercados quedaron escritos de once formas: `Chiclayo-moshoqueque` y
 * `Chiclayo - moshoqueque`, `Mercado central de Piura` y `Mercado central Piura`,
 * `Mercado modelo tumbes` y `Mercado modelo de tumbes`. Agrupar por el texto crudo da once
 * mercados donde hay siete, y el reporte que sale de ahí no se puede leer.
 *
 * ## La regla, y por qué es tan conservadora
 *
 * Fusionar de más es MUCHO peor que fusionar de menos, y no es simétrico:
 *
 * - Un grupo de más se ve: aparece dos veces el mismo mercado y cualquiera lo nota.
 * - Un grupo de menos NO se ve: dos mercados distintos suman sus cifras bajo un solo nombre y el
 *   número resultante es plausible. Nadie lo revisa. Es exactamente el fracaso que este proyecto
 *   ya pagó el 15-set, cuando un agente contestó con total seguridad sobre la tabla equivocada.
 *
 * Por eso: la regla no hace nada inteligente, la tabla de alias es lo más corta posible, y ante un
 * valor que no reconoce **la pantalla lo muestra tal cual y lo marca «sin normalizar»** en vez de
 * meterlo a la fuerza en el grupo más parecido.
 */

// ═══════════════════════════════════════════════════════════════════════════
// La clave
// ═══════════════════════════════════════════════════════════════════════════

/**
 * La clave con la que se agrupa un texto escrito a mano.
 *
 * ⚠️ **NUNCA SE MUESTRA EN PANTALLA.** Quita tildes, así que «Peña» y «Pena» colapsan: perfecto
 * para agrupar, inaceptable para rotular. El rótulo sale de `rotuloDeGrupo()`.
 *
 * Qué hace, en orden: quita tildes, baja a minúsculas, reemplaza todo lo que no sea letra o
 * número por un espacio (así `Chiclayo-moshoqueque` y `Chiclayo - moshoqueque` caen en lo mismo),
 * **deduplica** los tokens y los **ordena alfabéticamente**.
 *
 * Lo de ordenar es lo que hace que `Oviedo Anita` y `Anita Oviedo` sean la misma persona, y que
 * `Mercado central de Piura` y `Mercado central Piura` sean el mismo mercado una vez que `de` sale
 * como palabra de relleno.
 */
export function claveTexto(
  v: string | null | undefined, quitar: readonly string[] = [],
): string | null {
  if (v == null) return null;
  const sinTildes = v.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ç/gi, 'c');
  const limpio = sinTildes.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  if (limpio === '') return null;
  const vistos = new Set<string>();
  for (const w of limpio.split(' ')) if (w !== '' && !quitar.includes(w)) vistos.add(w);
  const toks = [...vistos].sort();
  return toks.length === 0 ? null : toks.join(' ');
}

/**
 * Las palabras de relleno de un nombre de LUGAR.
 *
 * 🔴 **`norte`, `sur`, `este`, `oeste`, `centro`, `1` y `2` no están, y no pueden llegar a estar.**
 * `Mercado Unicachi Norte` (Comas) y `Mercado Unicachi Sur` (Villa El Salvador) son dos mercados
 * distintos que la lista cerrada del formulario ya trae separados. Meter los puntos cardinales acá
 * los fusionaría y el error sería invisible: un solo «Unicachi» con el doble de fichas.
 *
 * Hay una prueba que se pone roja si alguien los agrega.
 */
export const STOPWORDS_LUGAR: readonly string[] = [
  'mercado', 'mercados', 'mdo', 'mcdo', 'de', 'del', 'la', 'el', 'los', 'las',
  'nro', 'no', 'num', 'numero',
];

/**
 * Personas: lista **vacía**, y es una decisión.
 *
 * Ningún token de un nombre es ruido. `de` y `del` son apellidos peruanos corrientes —«De la
 * Cruz», «Del Águila»— y sacarlos convierte a dos personas distintas en una.
 */
export const STOPWORDS_PERSONA: readonly string[] = [];

// ═══════════════════════════════════════════════════════════════════════════
// Rótulos y alias
// ═══════════════════════════════════════════════════════════════════════════

/**
 * El rótulo congelado de cada clave conocida.
 *
 * **Congelado, y ahí está el punto.** La alternativa obvia —rotular con la grafía más frecuente—
 * tiene un defecto que solo se ve con el tiempo: el rótulo cambiaría solo al entrar fichas nuevas,
 * y el reporte de esta semana dejaría de poder compararse con el de la anterior sin que nadie
 * tocara nada.
 */
export const ROTULOS_MERCADO: Record<string, string> = {
  'chiclayo moshoqueque': 'Mercado Moshoqueque (Chiclayo)',
  'moshoqueque': 'Mercado Moshoqueque (Chiclayo)',
  'capullanas': 'Mercado Las Capullanas (Piura)',
  'capullanas las': 'Mercado Las Capullanas (Piura)',
  'central piura': 'Mercado Central de Piura',
  'modelo tumbes': 'Mercado Modelo de Tumbes',
  'grau': 'Mercado Grau (Tacna)',
  'polvos rosados': 'Mercado Polvos Rosados (Tacna)',
  'polvo rosados': 'Mercado Polvos Rosados (Tacna)',
};

/**
 * Lo que la regla NO puede resolver sola, y por qué.
 *
 * `canonico: null` significa **«esto es una pregunta, no una respuesta»**: la pantalla lo muestra
 * con la nota al lado en vez de adivinar. Una tabla de alias que adivina es una tabla que miente
 * en silencio.
 */
export const ALIAS_PENDIENTES: readonly { escrito: string; nota: string }[] = [
  { escrito: 'Mercado Piura',
    nota: 'Ambiguo: es la ciudad, no el mercado. Piura tiene Central, Modelo, Las Capullanas y '
      + 'Anexo. Se puede confirmar por el centroide GPS contra «Mercado Central de Piura».' },
  { escrito: 'A',
    nota: 'Nombre de supervisora truncado a una inicial. Se confirma viendo quién trabajó ese '
      + 'mercado ese día; no se fusiona por parecido.' },
];

// ═══════════════════════════════════════════════════════════════════════════
// Distancia
// ═══════════════════════════════════════════════════════════════════════════

/** Haversine, en kilómetros. Sin librería: son seis líneas y no justifica una dependencia. */
export function distanciaKm(
  aLat: number, aLng: number, bLat: number, bLng: number,
): number {
  const R = 6371;
  const rad = (g: number) => (g * Math.PI) / 180;
  const dLat = rad(bLat - aLat);
  const dLng = rad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** El promedio de las coordenadas que existen. `null` si ninguna ficha del grupo tiene GPS. */
export function centroide(
  puntos: readonly { lat: number | null; lng: number | null }[],
): { lat: number; lng: number } | null {
  const con = puntos.filter(
    (p): p is { lat: number; lng: number } => p.lat !== null && p.lng !== null,
  );
  if (con.length === 0) return null;
  return {
    lat: con.reduce((s, p) => s + p.lat, 0) / con.length,
    lng: con.reduce((s, p) => s + p.lng, 0) / con.length,
  };
}

/** A cuántos km del centro de Lima. Sirve para saber si una ficha es de provincia. */
const LIMA = { lat: -12.05, lng: -77.04 };
export const esDeProvincia = (lat: number | null, lng: number | null): boolean =>
  lat !== null && lng !== null && distanciaKm(lat, lng, LIMA.lat, LIMA.lng) > 80;

/** Cuánto pueden separarse dos fichas del mismo nombre antes de que deje de ser el mismo sitio. */
export const RADIO_MISMO_SITIO_KM = 5;

// ═══════════════════════════════════════════════════════════════════════════
// Agrupar
// ═══════════════════════════════════════════════════════════════════════════

export interface FichaUbicada {
  /** El texto tal cual lo escribió (o eligió) el campo. */
  crudo: string | null;
  lat: number | null;
  lng: number | null;
}

export interface GrupoMercado {
  clave: string;
  /** Lo que se muestra. Nunca la clave. */
  rotulo: string;
  /** Todas las grafías del grupo, de la más frecuente a la menos. */
  grafias: { texto: string; n: number }[];
  n: number;
  /** No hay rótulo canónico: el rótulo es la grafía más frecuente y puede cambiar mañana. */
  sinNormalizar: boolean;
  /** Dos fichas con el mismo nombre a más de 5 km: se partió el grupo. */
  mismoNombreDosSitios: boolean;
  /** Eligió un mercado de la lista cerrada pero el GPS lo pone en otra parte. */
  rotuloNoCoincideConGps: boolean;
  centro: { lat: number; lng: number } | null;
}

/**
 * Agrupa por clave y **parte el grupo cuando la geografía dice que no es el mismo sitio**.
 *
 * ## Por qué hace falta la salvaguarda de GPS
 *
 * «Mercado Modelo» existe en Tumbes, en Chiclayo y en Piura. El día que alguien escriba solo
 * `Mercado modelo`, la clave es `modelo` y las tres ciudades se funden en una. Un nombre no
 * alcanza para afirmar identidad; la posición sí.
 *
 * ## Y por qué además se comprueba el rótulo de la lista cerrada
 *
 * Hay siete fichas que dicen `Mercado Túpac Amaru` —de Comas, Lima— y cuyo GPS está a 1,1 km de
 * Moshoqueque, en Chiclayo. Alguien eligió el primer nombre de una lista que no tenía su mercado.
 * Sin esta comprobación la pantalla imprimiría «Mercado Túpac Amaru · 7 fichas» con la máxima
 * confianza, porque *viene de la lista cerrada*: el número seguro sobre la fuente equivocada, otra
 * vez. Acá se agrupa igual, pero se marca.
 *
 * ⚠️ **Las fichas sin coordenadas no parten nada.** Un grupo donde solo algunas tienen GPS se
 * compara usando las que lo tienen; si no tiene ninguna, no se puede afirmar nada y no se marca.
 * Marcar por ausencia de dato es inventar un hallazgo.
 */
export function agruparMercados(fichas: readonly FichaUbicada[]): GrupoMercado[] {
  const porClave = new Map<string, FichaUbicada[]>();
  for (const f of fichas) {
    const k = claveTexto(f.crudo, STOPWORDS_LUGAR);
    if (k === null) continue;
    const lista = porClave.get(k);
    if (lista === undefined) porClave.set(k, [f]); else lista.push(f);
  }

  const salida: GrupoMercado[] = [];
  for (const [clave, lista] of porClave) {
    for (const parte of partirPorDistancia(lista)) {
      salida.push(armarGrupo(clave, parte.fichas, parte.partido));
    }
  }
  return salida.sort((a, b) => b.n - a.n || a.rotulo.localeCompare(b.rotulo, 'es'));
}

/**
 * Parte una lista de fichas de la misma clave en racimos que estén a menos de 5 km entre sí.
 *
 * Es un agrupamiento por cercanía, deliberadamente simple: se recorre y cada ficha entra al primer
 * racimo cuyo centro esté a menos del radio. Las que no tienen coordenadas van al racimo más
 * grande, porque no pueden contradecir a nadie.
 */
function partirPorDistancia(
  lista: readonly FichaUbicada[],
): { fichas: FichaUbicada[]; partido: boolean }[] {
  const conGps = lista.filter((f) => f.lat !== null && f.lng !== null);
  const sinGps = lista.filter((f) => f.lat === null || f.lng === null);
  if (conGps.length === 0) return [{ fichas: [...lista], partido: false }];

  const racimos: FichaUbicada[][] = [];
  for (const f of conGps) {
    const donde = racimos.find((r) => {
      const c = centroide(r);
      return c !== null && distanciaKm(c.lat, c.lng, f.lat as number, f.lng as number)
        <= RADIO_MISMO_SITIO_KM;
    });
    if (donde === undefined) racimos.push([f]); else donde.push(f);
  }

  const partido = racimos.length > 1;
  // Las que no tienen GPS no pueden asignarse a un sitio: van al racimo mayor, que es donde menos
  // distorsionan, y el grupo queda marcado igual para que quien lo lea sepa que hay mezcla.
  const mayor = racimos.reduce((a, b) => (b.length > a.length ? b : a), racimos[0]);
  mayor.push(...sinGps);
  return racimos.map((r) => ({ fichas: r, partido }));
}

function armarGrupo(clave: string, fichas: FichaUbicada[], partido: boolean): GrupoMercado {
  const cuenta = new Map<string, number>();
  for (const f of fichas) {
    const t = (f.crudo ?? '').trim();
    if (t !== '') cuenta.set(t, (cuenta.get(t) ?? 0) + 1);
  }
  const grafias = [...cuenta.entries()]
    .map(([texto, n]) => ({ texto, n }))
    .sort((a, b) => b.n - a.n || a.texto.localeCompare(b.texto, 'es'));

  const centro = centroide(fichas);
  const { rotulo, sinNormalizar, noCoincide } = rotuloDeGrupo(clave, grafias, centro);

  return {
    clave,
    rotulo,
    grafias,
    n: fichas.length,
    sinNormalizar,
    mismoNombreDosSitios: partido,
    rotuloNoCoincideConGps: noCoincide,
    centro,
  };
}

/**
 * De dónde sale el rótulo, en orden y de forma determinista:
 *
 * 1. **La lista cerrada del formulario, si el GPS la respalda.** Es el nombre oficial y el que el
 *    cliente reconoce — pero solo si la ficha está donde ese mercado está. Si no, se usa igual
 *    (es lo que el campo eligió) y se marca `rotuloNoCoincideConGps`, porque tapar la elección
 *    sería perder el rastro de cómo se registró.
 * 2. **`ROTULOS_MERCADO`**, la tabla congelada.
 * 3. **La grafía más frecuente**, y el grupo sale marcado «sin normalizar».
 *
 * Solo el caso 3 cambia cuando entra data nueva, y va marcado justamente por eso.
 */
function rotuloDeGrupo(
  clave: string,
  grafias: readonly { texto: string; n: number }[],
  centro: { lat: number; lng: number } | null,
): { rotulo: string; sinNormalizar: boolean; noCoincide: boolean } {
  const deLista = MERCADOS_LIMA.find(
    (m) => grafias.some((g) => claveTexto(g.texto, STOPWORDS_LUGAR) === claveTexto(m.nom, STOPWORDS_LUGAR)),
  );
  if (deLista !== undefined) {
    const noCoincide = centro !== null
      && distanciaKm(centro.lat, centro.lng, deLista.lat, deLista.lng) > RADIO_MISMO_SITIO_KM;
    return { rotulo: deLista.nom, sinNormalizar: false, noCoincide };
  }
  const congelado = ROTULOS_MERCADO[clave];
  if (congelado !== undefined) return { rotulo: congelado, sinNormalizar: false, noCoincide: false };
  return {
    rotulo: grafias[0]?.texto ?? clave,
    sinNormalizar: true,
    noCoincide: false,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Personas
// ═══════════════════════════════════════════════════════════════════════════

export interface GrupoPersona {
  clave: string;
  /** La grafía más frecuente. A una persona se la rotula como ella se escribe. */
  rotulo: string;
  grafias: { texto: string; n: number }[];
  n: number;
}

/**
 * Agrupa supervisores por nombre, sin salvaguarda geográfica: una persona sí puede estar en dos
 * ciudades distintas la misma semana, y de hecho es lo que hacen.
 */
export function agruparPersonas(nombres: readonly (string | null)[]): GrupoPersona[] {
  const porClave = new Map<string, Map<string, number>>();
  for (const n of nombres) {
    const k = claveTexto(n, STOPWORDS_PERSONA);
    if (k === null) continue;
    const t = (n ?? '').trim();
    const m = porClave.get(k) ?? new Map<string, number>();
    m.set(t, (m.get(t) ?? 0) + 1);
    porClave.set(k, m);
  }
  return [...porClave.entries()].map(([clave, m]) => {
    const grafias = [...m.entries()].map(([texto, n]) => ({ texto, n }))
      .sort((a, b) => b.n - a.n || a.texto.localeCompare(b.texto, 'es'));
    return {
      clave,
      rotulo: grafias[0]?.texto ?? clave,
      grafias,
      n: grafias.reduce((s, g) => s + g.n, 0),
    };
  }).sort((a, b) => b.n - a.n || a.rotulo.localeCompare(b.rotulo, 'es'));
}
